import { Button, Dialog, Hidden, IconButton, Stack, Switch, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Link as RouterLink } from 'react-router-dom';
import { useHistory } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import { DataTable } from '../../DataTable';
import { useEffect, useState, useMemo, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmationDialog } from '../../commonComponents';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { strategyListingType, StrategyType } from '../../../redux-store/types/api-types';
import {
  enableDisableStrategy,
  getStarategyTableList,
  updateStarategyById,
} from '../../../redux-store/actions/strategies';
import { RootStateType } from '../../../redux-store/reducers';
import { CursorDirection } from '../../../utils/constant';

export const AccessHeaderForFund = (): JSX.Element => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Box display={'flex'} flexDirection={'column'}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: 14,
            color: 'primary.main',
            fontWeight: 500,
          }}
          component="span">
          Access
          <InfoOutlinedIcon
            color="info"
            fontSize="small"
            onClick={handleOpen}
            sx={{ position: 'relative', top: 4 }}
          />
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: 12,
            color: 'primary.main',
            fontWeight: 500,
          }}>
          Enable/Disable
        </Typography>
      </Box>
      <Dialog
        onClose={handleClose}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            maxWidth: 550,
            p: { xs: 2, sm: '15px 20px 45px' },
            borderRadius: '10px',
            height: 'unset',
            overflowY: 'auto',
          },
        }}>
        <IconButton onClick={handleClose} sx={{ alignSelf: 'flex-end' }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
          <InfoOutlinedIcon color="info" fontSize="large" />
        </Box>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
              textAlign: 'center',
            },
          }}>
          <Typography sx={{ color: 'secondary.main', lineHeight: '35px', mt: 2 }}>
            Please ensure that you have at least one active Strategy before disabling the Strategy.
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default function StrategyList(): JSX.Element {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);

  const [strategyData, setStrategyData] = useState<StrategyType[]>();

  const history = useHistory();

  const [openDialog, setDialog] = useState<{ StrategyData: StrategyType } | null>(null);
  const {
    page: storePage,
    sortDirection: storeSortDirection,
    rowsPerPage: storeRowsPerPage,
    nextCursor: storeNextCursor,
    prevCursor: storePrevCursor,
    cursorDirection: storeCursorDirection,
    hasNext: storeHasNext,
    hasPrev: storeHasPrev,
    currentCursor: storeCurrentCursor,
    paginationRole,
  } = useSelector((store: RootStateType) => store.paramsObj);

  const isPaginationRoleIsDistributor = paginationRole === 'STRATEGIES';

  const [currentPage, setCurrentPage] = useState(
    isPaginationRoleIsDistributor ? storePage || 1 : 1
  );
  const [hasNext, setHasNext] = useState(
    isPaginationRoleIsDistributor ? storeHasNext || false : false
  );
  const [hasPrev, setHasPrev] = useState(
    isPaginationRoleIsDistributor ? storeHasPrev || false : false
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    isPaginationRoleIsDistributor ? storeRowsPerPage || 10 : 10
  );
  const [currentCursor, setCurrentCursor] = useState<string | null>(
    isPaginationRoleIsDistributor ? storeCurrentCursor || null : null
  );
  const [nextCursor, setNextCursor] = useState<string | null>(
    isPaginationRoleIsDistributor ? storeNextCursor || null : null
  );
  const [prevCursor, setPrevCursor] = useState<string | null>(
    isPaginationRoleIsDistributor ? storePrevCursor || null : null
  );
  const [cursorDirection, setCursorDirection] = useState<CursorDirection | undefined>(undefined);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setLoading(true);
        const params = {
          limit: rowsPerPage,
          order: 'DESC',
          ...(currentCursor
            ? {
                cursor: currentCursor,
                ...(cursorDirection && { direction: cursorDirection }),
              }
            : {
                cursor: null,
              }),
        };
        const res = (await dispatch(
          getStarategyTableList(params)
        )) as unknown as strategyListingType;
        const { data, nextCursor: nextC, prevCursor: prevC, hasNext, hasPrev } = res || {};
        if (!isComponentActive) {
          return;
        }

        setNextCursor(nextC || '');
        setPrevCursor(prevC || '');
        setHasNext(hasNext || false);
        setHasPrev(hasPrev || false);
        setStrategyData(data.length ? data : []);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.log((e as Error).message);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [currentPage, rowsPerPage, currentCursor, cursorDirection]);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page
  };

  const handleCursorPageChange = (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
    // If user clicks next
    if (newPage > currentPage - 1 && nextCursor) {
      setCurrentCursor(nextCursor);
      setCursorDirection(CursorDirection.NEXT);
    }
    // If user clicks prev
    else if (newPage < currentPage - 1 && prevCursor) {
      setCurrentCursor(prevCursor);
      setCursorDirection(CursorDirection.PREV);
    }
    // Optionally update currentPage if you want to show the page number
  };
  const AccessViewOrEditCell = [
    {
      header: AccessHeaderForFund,
      renderCell: (row: StrategyType): JSX.Element => {
        return (
          <>
            <Stack
              sx={{
                alignItems: { xs: 'baseline', sm: 'center' },
                '.MuiSwitch-root': {
                  paddingLeft: { xs: '0px', sm: '12px' },
                  width: { xs: '52px', sm: '58px' },
                },
                '.MuiSwitch-switchBase': { paddingLeft: { xs: '0px', sm: '9px' } },
              }}>
              <Switch
                sx={{ '.MuiSwitch-switchBase': { color: 'black' } }}
                checked={row.isActive || false}
                onChange={() => setDialog({ StrategyData: row })}
                disabled={true}
              />
            </Stack>
          </>
        );
      },
    },
  ];

  const columns: any = [
    {
      header: 'Strategy Name',
      renderCell: (row: StrategyType) => {
        return (
          <Typography
            variant="h5"
            sx={{
              color: '#61D1D6',
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: '14px',
              ':hover': {
                color: 'primary.main',
              },
            }}
            component={RouterLink}
            to={{ pathname: 'strategy-details', state: { strategyId: row.id } }}>
            {row.strategyName || 'N/A'}
          </Typography>
        );
      },
    },
  ];

  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return (
    <>
      <Box sx={{ bgcolor: 'common.white', px: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: { xs: 2, sm: 7 },
            pb: { xs: 2, sm: 4 },
          }}>
          <Typography sx={{ fontSize: { xs: 15, sm: 25 }, fontWeight: 500 }}>Strategies</Typography>
          {/* {access && ( */}
          {/* <Hidden only="xs">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                color: 'white',
                fontWeight: 500,
                fontSize: 14,
                mr: 2.5,
                py: 0.5,
                px: 3,
              }}
              onClick={() => history.push('add-strategy')}>
              Add Strategy
            </Button>
          </Hidden> */}
          {/* )} */}
          {/* {access && ( */}
          {/* <Hidden smUp={true}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                fontSize: 13,
                py: 0.5,
              }}
              onClick={() => history.push('add-strategy')}>
              Add Strategy
            </Button>
          </Hidden> */}
          {/* )} */}
        </Box>
        {useMemo(
          () => (
            <DataTable
              tableData={strategyData || []}
              tableHeader={[...columns, ...AccessViewOrEditCell]}
              currentPage={currentPage}
              noOfPages={1}
              isLoading={isLoading}
              paginationAction={handlePageChange}
              renderAdditionalRow={true}
              tableBodyCustomStyles={{
                '.MuiTableCell-root': {
                  borderBottom: 'none',
                },
              }}
              tableHeaderCustomStyles={{
                '&:last-child th': { border: 0 },
                '.MuiTableCell-head': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'primary.main',
                },
              }}
              rowCustomStyles={{
                '&:last-child td, &:last-child th': { border: 0 },
                '.MuiTableCell-body ': { color: '#686e72' },
                background: 'transparent',
              }}
              handleCursorPageChange={handleCursorPageChange}
              hasNext={hasNext}
              hasPrev={hasPrev}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              rowsPerPage={rowsPerPage}
            />
          ),
          [columns, isLoading]
        )}
      </Box>
      <ConfirmationDialog
        message={`Are you sure you want to ${
          openDialog?.StrategyData.isActive ? 'disable' : 'enable'
        } this Strategy ?`}
        open={openDialog !== null}
        setOpen={() => setDialog(null)}
        onSave={async () => {
          setDialog(null);
          try {
            if (openDialog) {
              await dispatch(
                enableDisableStrategy({
                  id: openDialog?.StrategyData.id,
                  status: openDialog?.StrategyData.isActive ? 'disable' : 'enable',
                })
              );
              const updatedFeeSlabList = strategyData?.map((row) => {
                if (row.id === openDialog?.StrategyData.id) {
                  return { ...row, isActive: !openDialog?.StrategyData.isActive };
                }
                return row;
              });
              setStrategyData(updatedFeeSlabList);
            }
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setDialog(null)}
      />
    </>
  );
}
